import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarCheck, Loader2, MessageCircle } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { API, SERVICES, WHATSAPP_URL } from "@/lib/site";

const EMPTY = {
  name: "",
  phone: "",
  car_model: "",
  preferred_date: "",
  service_type: SERVICES[0],
  message: "",
};

const inputCls =
  "border-white/10 bg-white/5 text-white placeholder:text-white/30 focus-visible:ring-gold focus-visible:ring-1 focus-visible:ring-offset-0";

export const BookingDialog = ({ open, onOpenChange, presetService }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({
        ...EMPTY,
        name: f.name,
        phone: f.phone,
        service_type: presetService || SERVICES[0],
      }));
    }
  }, [open, presetService]);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Inspection booked!", {
        description: "We'll call you shortly to confirm your slot.",
      });
      onOpenChange(false);
      setForm(EMPTY);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error("Could not submit booking", {
        description:
          typeof detail === "string"
            ? detail
            : "Please check your details or book via WhatsApp.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-dialog"
        className="max-w-md border-white/10 bg-[#0c0c0c] text-white sm:rounded-3xl"
      >
        <DialogHeader>
          <div className="mb-2 grid h-11 w-11 place-items-center rounded-xl border border-gold/30 bg-gold/10 text-gold">
            <CalendarCheck className="h-5 w-5" />
          </div>
          <DialogTitle className="font-heading text-2xl font-extrabold tracking-tight">
            Book an Inspection
          </DialogTitle>
          <DialogDescription className="text-white/50">
            Same-day slots available across Hyderabad. We confirm every booking
            with a quick call.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="mt-2 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="bk-name" className="text-white/70">
              Your Name
            </Label>
            <Input
              id="bk-name"
              data-testid="booking-name-input"
              required
              minLength={2}
              placeholder="e.g. Syed Ahmed"
              value={form.name}
              onChange={set("name")}
              className={inputCls}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bk-phone" className="text-white/70">
              Phone Number
            </Label>
            <Input
              id="bk-phone"
              data-testid="booking-phone-input"
              required
              type="tel"
              pattern="[0-9+ ]{8,16}"
              placeholder="e.g. 79895 67909"
              value={form.phone}
              onChange={set("phone")}
              className={inputCls}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="bk-car" className="text-white/70">
                Car Model
              </Label>
              <Input
                id="bk-car"
                data-testid="booking-car-model-input"
                required
                minLength={2}
                placeholder="e.g. Creta 2023"
                value={form.car_model}
                onChange={set("car_model")}
                className={inputCls}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bk-date" className="text-white/70">
                Preferred Date
              </Label>
              <Input
                id="bk-date"
                data-testid="booking-date-input"
                required
                type="date"
                min={new Date().toISOString().split("T")[0]}
                value={form.preferred_date}
                onChange={set("preferred_date")}
                className={`${inputCls} [color-scheme:dark]`}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-white/70">Service Type</Label>
            <Select
              value={form.service_type}
              onValueChange={(v) =>
                setForm((f) => ({ ...f, service_type: v }))
              }
            >
              <SelectTrigger
                data-testid="booking-service-select"
                className={inputCls}
              >
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent className="border-white/10 bg-[#141414] text-white">
                {SERVICES.map((s) => (
                  <SelectItem
                    key={s}
                    value={s}
                    data-testid={`booking-service-option-${s
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")}`}
                    className="focus:bg-gold/15 focus:text-gold"
                  >
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bk-msg" className="text-white/70">
              Notes <span className="text-white/30">(optional)</span>
            </Label>
            <Textarea
              id="bk-msg"
              data-testid="booking-message-input"
              rows={2}
              placeholder="Location, seller details, anything we should know"
              value={form.message}
              onChange={set("message")}
              className={`${inputCls} resize-none`}
            />
          </div>

          <button
            data-testid="booking-submit-button"
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3.5 font-heading text-sm font-extrabold text-black transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold-light disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? "Booking..." : "Confirm Booking"}
          </button>

          <a
            data-testid="booking-whatsapp-link"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 text-sm font-semibold text-white/50 transition-colors duration-300 hover:text-[#25D366]"
          >
            <MessageCircle className="h-4 w-4" />
            Prefer WhatsApp? Message us instead
          </a>
        </form>
      </DialogContent>
    </Dialog>
  );
};
